import { useEffect, useRef, useState } from "react";
import {
    FaChevronDown,
    FaMagnifyingGlass,
    FaUser,
} from "react-icons/fa6";
import { useHookUser } from "../../../hooks/useHookUser";

export const SelectUser = ({
    label,
    value,
    setValue,
    onChange,
    placeholder = "Pilih Pengguna",
    searchable = true,
}) => {
    const hookResult = useHookUser();

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const wrapperRef = useRef(null);

    // Support {data:[...]} atau langsung array
    const rawData = hookResult?.data?.data
        ? hookResult.data.data
        : hookResult?.data;

    const safeData = Array.isArray(rawData) ? rawData : [];

    // Close klik luar
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target)
            ) {
                setOpen(false);
                setSearch("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () =>
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
    }, []);

    // Selected support ID / Object
    const selected = safeData.find((item) => {
        if (typeof value === "object" && value !== null) {
            return String(item.id) === String(value.id);
        }

        return String(item.id) === String(value);
    });

    // Filter
    const filteredOptions = safeData.filter((o) => {
        const keyword = search.toLowerCase();

        return (
            (o?.name ?? "").toLowerCase().includes(keyword) ||
            (o?.username ?? "").toLowerCase().includes(keyword) ||
            (o?.email ?? "").toLowerCase().includes(keyword)
        );
    });

    const handleSelect = (selectedValue) => {
        setOpen(false);
        setSearch("");

        let payload = null;

        if (selectedValue !== "") {
            payload = safeData.find(
                (item) =>
                    String(item.id) === String(selectedValue)
            );
        }

        if (onChange) {
            onChange(payload);
        } else {
            setValue?.(payload);
        }
    };

    return (
        <div className="w-full relative" ref={wrapperRef}>
            {label && (
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                    {label}
                </label>
            )}

            {/* Select Box */}
            <div
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-800/60 border border-slate-600/40 cursor-pointer transition-all duration-200"
                style={{
                    borderColor: open
                        ? "rgba(0,180,216,.5)"
                        : "rgba(71,85,105,.4)",
                    boxShadow: open
                        ? "0 0 0 1px rgba(0,180,216,.2)"
                        : "none",
                }}
            >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FaUser className="text-slate-500 text-xs flex-shrink-0" />

                    <span
                        className={`truncate ${
                            selected
                                ? "text-gray-100"
                                : "text-slate-500"
                        }`}
                    >
                        {selected
                            ? selected.name
                            : placeholder}
                    </span>
                </div>

                <FaChevronDown
                    className={`text-xs text-slate-500 transition-transform duration-200 ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </div>


            {/* Dropdown */}
            {open && (
                <div
                    className="absolute z-[1000] mt-2 w-full rounded-xl overflow-hidden shadow-2xl shadow-black/40"
                    style={{
                        background: "rgba(15,23,42,.95)",
                        border: "1px solid rgba(51,65,85,.5)",
                        backdropFilter: "blur(16px)",
                    }}
                >

                    {/* Search */}
                    {searchable && (
                        <div
                            className="p-2.5"
                            style={{
                                borderBottom:
                                    "1px solid rgba(51,65,85,.3)",
                            }}
                        >
                            <div className="relative">
                                <FaMagnifyingGlass
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs"
                                />

                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="Cari pengguna..."
                                    value={search}
                                    onChange={(e) =>
                                        setSearch(
                                            e.target.value
                                        )
                                    }
                                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-slate-800/80 border border-slate-600/30 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                />
                            </div>
                        </div>
                    )}


                    {/* List */}
                    <div className="max-h-60 overflow-y-auto py-1">

                        {/* Reset */}
                        <div
                            onClick={() => handleSelect("")}
                            className={`mx-1 px-3 py-2.5 rounded-lg cursor-pointer text-sm transition-all flex items-center gap-2 ${
                                !selected
                                    ? "bg-cyan-600/15 text-cyan-400 font-semibold"
                                    : "text-slate-400 hover:bg-slate-700/40 hover:text-gray-100"
                            }`}
                        >
                            <FaUser className="text-xs" />

                            <span>
                                Semua Pengguna
                            </span>
                        </div>


                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-6 text-center">
                                <p className="text-slate-500 text-sm italic">
                                    Pengguna tidak ditemukan
                                </p>
                            </div>
                        ) : (
                            filteredOptions.map((opt) => {

                                const isSelected =
                                    selected?.id === opt.id;

                                return (
                                    <div
                                        key={opt.id}
                                        onClick={() =>
                                            handleSelect(
                                                opt.id
                                            )
                                        }
                                        className={`mx-1 px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                                            isSelected
                                                ? "bg-cyan-600/15 text-cyan-400"
                                                : "text-slate-300 hover:bg-slate-700/40 hover:text-gray-100"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-3">

                                            <div className="flex items-center gap-2 min-w-0">

                                                <FaUser className="text-xs opacity-60 flex-shrink-0" />

                                                <div className="min-w-0">

                                                    <div className="truncate font-medium">
                                                        {opt.name ?? "-"}
                                                    </div>

                                                    {(opt.username || opt.email) && (
                                                        <div className="text-[11px] text-slate-500 truncate">
                                                            {opt.username ??
                                                                opt.email}
                                                        </div>
                                                    )}

                                                </div>

                                            </div>


                                            {opt?.role?.name && (
                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-slate-400 whitespace-nowrap">
                                                    {opt.role?.name}
                                                </span>
                                            )}

                                        </div>
                                    </div>
                                );
                            })
                        )}

                    </div>
                </div>
            )}
        </div>
    );
};